/**
 * UI preferences that authkit-ui and insights-fe both read.
 *
 * The two are different hosts under one registrable domain in every deployed
 * environment (authkit.meqinsights.com / meqinsights.com, and the staging and
 * demo pairs), so a host-scoped cookie cannot cross between them. Writing to
 * the shared parent lets a choice made on either surface survive the redirect.
 * Locally both are `localhost`, where cookies ignore the port and a Domain
 * attribute is both unnecessary and unreliable — hence the undefined.
 *
 * Deliberately not localStorage: that is origin-scoped with no way to widen
 * it. Only non-sensitive display preferences belong here — the cookie is
 * readable by every subdomain and is not an auth surface.
 *
 * authkit-ui carries a mirror of this file; the domain rule must match or a
 * preference set on one surface is invisible to the other.
 */
const SHARED_COOKIE_DOMAIN = "meqinsights.com";

const ONE_YEAR_SECONDS = 60 * 60 * 24 * 365;

export function sharedCookieDomain(hostname: string): string | undefined {
  return hostname === SHARED_COOKIE_DOMAIN ||
    hostname.endsWith(`.${SHARED_COOKIE_DOMAIN}`)
    ? SHARED_COOKIE_DOMAIN
    : undefined;
}

export function readPreferenceCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie
    .split("; ")
    .find((entry) => entry.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.slice(name.length + 1)) : null;
}

export function writePreferenceCookie(name: string, value: string): void {
  if (typeof document === "undefined") return;
  const domain = sharedCookieDomain(window.location.hostname);
  document.cookie = [
    `${name}=${encodeURIComponent(value)}`,
    "path=/",
    `max-age=${ONE_YEAR_SECONDS}`,
    "samesite=lax",
    ...(domain ? [`domain=${domain}`] : []),
    ...(window.location.protocol === "https:" ? ["secure"] : []),
  ].join("; ");
}
